const formatters = new Map()

export function parseWorkspaceDate(value) {
    if (value === null || value === undefined || value === '') return null
    // Legacy SQL timestamps are UTC, just like the explicit ISO timestamps from the workspace API.
    const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value)
        ? value.replace(' ', 'T') + 'Z'
        : value
    const date = new Date(normalized)
    return Number.isNaN(date.getTime()) ? null : date
}

const instants = new Map()
// Parsed instants are cached: a timeline sort compares the same few hundred timestamps many times.
function instantOf(value) {
    if (!instants.has(value)) {
        if (instants.size > 5000) instants.clear()
        instants.set(value, parseWorkspaceDate(value)?.getTime() ?? -Infinity)
    }
    return instants.get(value)
}

// Messages in chronological order. Timestamps are compared as instants, so SQL and ISO strings, offsets
// and fractional seconds mix safely. Missing or unreadable dates sort first, which keeps the order total;
// rows without a kind (old history) still sort instead of throwing.
export function compareWorkspaceMessages(a, b) {
    const at = instantOf(a.date_created), bt = instantOf(b.date_created)
    const byTime = at === bt ? 0 : at < bt ? -1 : 1
    return byTime || String(a.kind || '').localeCompare(String(b.kind || '')) || a.id - b.id
}

export function workspaceTimeZone(companies, companyId) {
    const company = companies?.find(item => String(item.company_id) === String(companyId))
    const candidate = company?.time_zone || company?.timezone
    if (candidate) {
        try {
            return new Intl.DateTimeFormat('ru-RU', { timeZone: candidate }).resolvedOptions().timeZone
        } catch (error) {
            // A malformed optional company zone must not prevent opening the conversation.
        }
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

export function formatWorkspaceDate(value, options = null, timeZone = workspaceTimeZone(), fallback = '') {
    const date = parseWorkspaceDate(value)
    if (!date) return fallback
    const settings = {
        ...(options || { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }),
        timeZone,
    }
    const key = JSON.stringify(settings)
    if (!formatters.has(key)) {
        if (formatters.size >= 50) formatters.delete(formatters.keys().next().value)
        formatters.set(key, new Intl.DateTimeFormat('ru-RU', settings))
    }
    return formatters.get(key).format(date)
}

const MAX_PAYLOAD_LENGTH = 22 * 1024 * 1024

// Base64 file payloads; a declared PDF must really carry the PDF signature before it reaches a viewer.
export function decodeWorkspacePayload(payload, pdf = false) {
    if (typeof payload !== 'string' || !payload || payload.length > MAX_PAYLOAD_LENGTH)
        throw new Error('Файл пустой или превышает допустимый размер')
    const bytes = Uint8Array.from(atob(payload), char => char.charCodeAt(0))
    if (pdf && String.fromCharCode(...bytes.slice(0, 5)) !== '%PDF-')
        throw new Error('Содержимое файла не является PDF')
    return bytes
}

// Some browsers create PDF embeds with a fixed default size; cross-origin viewers size themselves.
export function fitPdfFrame(frame) {
    try {
        const doc = frame?.contentDocument,
            embed = doc?.querySelector('embed[type="application/pdf"]')
        if (embed) {
            doc.documentElement.style.height = '100%'
            doc.body.style.cssText = 'height:100%;margin:0'
            embed.style.cssText = 'display:block;width:100%;height:100%;border:0'
        }
    } catch (error) {
        /* The browser controls the size of cross-origin document viewers. */
    }
}

export function workspaceDayLabel(value, timeZone, now = Date.now()) {
    const date = parseWorkspaceDate(value)
    if (!date) return ''
    const options = { day: 'numeric', month: 'long', year: 'numeric' }
    const label = formatWorkspaceDate(date, options, timeZone)
    return label === formatWorkspaceDate(now, options, timeZone) ? 'Сегодня' : label
}

export function workspaceShortTime(value, timeZone, now = Date.now()) {
    const date = parseWorkspaceDate(value)
    if (!date) return ''
    const minutes = Math.max(0, Math.floor((now - date.getTime()) / 60000))
    if (minutes < 1) return 'Сейчас'
    if (minutes < 60) return `${minutes} мин`
    if (minutes < 1440) return `${Math.floor(minutes / 60)} ч`
    return formatWorkspaceDate(date, { day: '2-digit', month: 'short' }, timeZone)
}
